import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell } from 'recharts';
import {
  ShoppingBag, Boxes, IndianRupee, TrendingUp, CalendarClock, Download, Receipt,
} from 'lucide-react';
import {
  dashboardBookings, dashboardUpcoming, dashboardPending, dashboardGeneral, listInvoices,
} from '../api/resources';
import { inr, downloadCSV } from '../utils/format';
import Spinner from '../components/ui/Spinner';
import DateInput from '../components/ui/DateInput';
import PageHeader from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/Badge';

const TABS = [
  { key: 'bookings', label: 'Bookings' },
  { key: 'upcoming', label: 'Upcoming Deliveries' },
  { key: 'pending', label: 'Pending Deliveries' },
  { key: 'general', label: 'General Details' },
];

const PIE_COLORS = ['#483476', '#6f55ac', '#e79b2e', '#059669', '#e11d48', '#0ea5e9', '#d17f18', '#8b5cf6'];

function StatCard({ icon: Icon, label, value, gradient, sub }) {
  return (
    <div className="stat-card flex items-center gap-4">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm ${gradient}`}>
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold uppercase tracking-wide text-content-subtle">{label}</p>
        <p className="truncate text-2xl font-bold text-content">{value}</p>
        {sub && <p className="truncate text-xs text-content-subtle">{sub}</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [tab, setTab] = useState('bookings');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rate, setRate] = useState('');

  const query = useQuery({
    queryKey: ['dash', tab, from, to, rate],
    queryFn: () => {
      const params = { from: from || undefined, to: to || undefined };
      if (tab === 'bookings') return dashboardBookings({ ...params, rate: rate || undefined });
      if (tab === 'upcoming') return dashboardUpcoming(params);
      if (tab === 'pending') return dashboardPending(params);
      return dashboardGeneral(params);
    },
  });

  const { data: recent } = useQuery({
    queryKey: ['dash-recent'],
    queryFn: () => listInvoices({ perPage: 5 }),
  });

  const data = query.data;

  const exportCSV = () => {
    if (tab === 'general') {
      downloadCSV('general.csv', ['Metric', 'Value'], [
        { Metric: 'Total Payments Received', Value: inr(data?.totalPayment || 0) },
        { Metric: 'Total Invoices', Value: data?.totalInvoices || 0 },
      ]);
    } else {
      downloadCSV(`${tab}-items.csv`, ['Item Name', 'Quantity'], (data?.items || []).map((i) => ({ 'Item Name': i.itemName, Quantity: i.qty })));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ShoppingBag}
        title="Dashboard"
        subtitle="Live overview of bookings, deliveries and payments"
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <Field label="From"><DateInput value={from} onChange={setFrom} /></Field>
            <Field label="To"><DateInput value={to} onChange={setTo} /></Field>
            {tab === 'bookings' && <Field label="Amount ≥ ₹"><input type="number" className="input" value={rate} onChange={(e) => setRate(e.target.value)} /></Field>}
            <button className="btn-primary !py-2 text-xs" onClick={() => query.refetch()}>Apply</button>
            <button className="btn-outline !py-2 text-xs" onClick={() => { setFrom(''); setTo(''); setRate(''); }}>Reset</button>
            <button className="btn-accent !py-2 text-xs" onClick={exportCSV} disabled={!data}><Download size={14} /> CSV</button>
          </div>
        }
      />

      {/* Segmented tabs */}
      <div className="inline-flex rounded-2xl border border-line bg-surface p-1 shadow-sm">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${tab === t.key ? 'bg-brand-50 font-semibold text-brand-700' : 'text-content-muted hover:bg-surface-hover hover:text-content'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {query.isLoading && <Spinner label="Crunching the numbers…" />}
      {query.isError && (
        <div className="card p-10 text-center text-sm text-content-muted">Could not load dashboard data.</div>
      )}
      {query.isSuccess && !query.isLoading && (
        <>
          {tab === 'general' ? (
            <GeneralPanel data={data} />
          ) : (
            <AnalyticsPanel tab={tab} data={data} recentInvoices={recent?.invoices || []} />
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-content-subtle">{label}</label>
      {children}
    </div>
  );
}

function AnalyticsPanel({ tab, data, recentInvoices }) {
  const summary = data?.summary || {};
  const avg = summary.orders ? summary.orderAmount / summary.orders : 0;

  // Revenue chart data — day number labels
  const chartData = useMemo(
    () => (data?.trend || []).map((d) => ({ ...d, day: String(d.date).slice(0, 2) })),
    [data?.trend],
  );

  // Donut — top 5 items by qty
  const donutData = useMemo(() => (data?.items || []).slice(0, 6), [data?.items]);

  // Upcoming flat list (events → invoices)
  const upcoming = useMemo(() => {
    const flat = [];
    for (const ev of data?.events || []) {
      for (const inv of ev.invoices || []) flat.push({ ...inv, date: ev.date });
    }
    return flat.slice(0, 5);
  }, [data?.events]);

  const stats = [
    { icon: ShoppingBag, label: 'Total Orders', value: summary.orders, gradient: 'grad-brand', sub: 'in selected period' },
    { icon: Boxes, label: 'Total Items', value: summary.items, gradient: 'grad-emerald', sub: 'pieces ordered' },
    { icon: IndianRupee, label: 'Order Amount', value: inr(summary.orderAmount), gradient: 'grad-accent', sub: 'gross bookings' },
    { icon: TrendingUp, label: 'Avg Order Value', value: inr(avg), gradient: 'grad-rose', sub: `${Math.round((avg / (summary.orderAmount || 1)) * 100)}% of total` },
  ];

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((s) => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Charts row */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-content">Revenue Trend</h3>
              <p className="text-xs text-content-subtle">Daily order amount · {tab}</p>
            </div>
            <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">{inr(summary.orderAmount)} total</span>
          </div>
          <div className="h-64">
            {chartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-content-subtle">No bookings in this period</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                  <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} width={36} />
                  <Tooltip
                    cursor={{ fill: '#f5f3fa' }}
                    formatter={(value) => [inr(value), 'Amount']}
                    labelFormatter={(l, p) => p?.[0]?.payload?.date || l}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(16,12,44,.12)', fontSize: 12 }}
                  />
                  <Bar dataKey="amount" fill="#6f55ac" radius={[5, 5, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Top items donut */}
        <div className="card p-5">
          <h3 className="font-display text-lg font-semibold text-content">Top Items</h3>
          <p className="text-xs text-content-subtle">Most ordered pieces</p>
          {donutData.length === 0 ? (
            <div className="flex h-56 items-center justify-center text-sm text-content-subtle">No items in this period</div>
          ) : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="qty" nameKey="itemName" innerRadius={48} outerRadius={68} paddingAngle={3} strokeWidth={2}>
                      {donutData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={(v, n) => [`${v} pcs`, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1.5">
                {donutData.slice(0, 4).map((it, i) => (
                  <div key={it.itemName} className="flex items-center gap-2 text-xs">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="flex-1 truncate text-content-muted">{it.itemName}</span>
                    <span className="font-bold text-content">{it.qty}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upcoming + recent */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-content">Upcoming Deliveries</h3>
            <CalendarClock size={18} className="text-content-subtle" />
          </div>
          {upcoming.length === 0 ? (
            <p className="py-6 text-center text-sm text-content-subtle">No deliveries scheduled.</p>
          ) : (
            <div className="space-y-2">
              {upcoming.map((u) => (
                <div key={u.id} className="flex items-center justify-between rounded-xl border border-line bg-surface-hover px-3 py-2.5">
                  <div>
                    <p className="text-sm font-semibold text-content">{u.orderNo}</p>
                    <p className="text-xs text-content-subtle">{u.date}</p>
                  </div>
                  <span className="text-sm font-bold text-brand-700">{inr(u.totalAmount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-content">Recent Bookings</h3>
            <span className="flex items-center gap-1 text-xs font-semibold text-brand-700"><Receipt size={14} /> Latest 5</span>
          </div>
          {recentInvoices.length === 0 ? (
            <p className="py-6 text-center text-sm text-content-subtle">No bookings yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-content-subtle">
                  <th className="pb-2">Invoice</th><th className="pb-2">Customer</th><th className="pb-2">Date</th><th className="pb-2 text-right">Amount</th><th className="pb-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-line">
                    <td className="py-2.5 font-semibold text-content">{inv.orderNo}</td>
                    <td className="py-2.5 text-content-muted">{inv.customer?.name || '—'}</td>
                    <td className="py-2.5 text-content-muted">{inv.orderDate}</td>
                    <td className="py-2.5 text-right font-semibold text-content">{inr(inv.totalAmount)}</td>
                    <td className="py-2.5 text-right"><StatusPill status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Item summary strip */}
      {(data?.items || []).length > 0 && (
        <div className="card flex flex-wrap items-center gap-x-6 gap-y-2 p-5">
          <span className="text-xs font-bold uppercase tracking-widest text-content-subtle">Item Summary</span>
          {(data?.items || []).slice(0, 8).map((it, i) => (
            <span key={it.itemName} className="flex items-center gap-1.5 text-sm">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
              <span className="text-content-muted">{it.itemName}</span>
              <b className="text-content">{it.qty}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- PRODUCTION (the floor at a glance) ---------------- */

/* ---------------- PERFORMANCE (the owner's view) ---------------- */

function StatusPill({ status }) {
  return <StatusBadge status={status} />;
}

function GeneralPanel({ data }) {
  const stats = [
    { icon: IndianRupee, label: 'Payments Received', value: inr(data?.totalPayment || 0), gradient: 'grad-emerald', sub: 'collected amount' },
    { icon: Receipt, label: 'Total Invoices', value: data?.totalInvoices || 0, gradient: 'grad-brand', sub: 'in current FY' },
    { icon: TrendingUp, label: 'Range Filtered', value: data?.calculated ? 'Yes' : 'No', gradient: 'grad-accent', sub: data?.calculated ? 'payments within range' : 'whole financial year' },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((s) => <StatCard key={s.label} {...s} />)}
      </div>
      <div className="card p-8 text-center">
        <Receipt size={32} className="mx-auto mb-3 text-brand-300" />
        <p className="font-display text-lg font-semibold text-content">Payment Receipts Summary</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-content-subtle">
          Set a date range above to calculate payments received within that period, or leave blank to cover the entire active financial year.
        </p>
      </div>
    </div>
  );
}
